/**
 * Shared business insight modules — used by Business Insights page and pump hub diagnostics.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminBusinessInsightsModule, friendlyFetchError } from './adminApi';
import {
  ActivityCharts,
  AllocationCharts,
  CashCharts,
  EmployeeCharts,
  ExpenseAnomalyCharts,
  InsightsPanel,
  NozzleCharts,
  ProfitabilityCharts,
  StaffingCharts,
} from './AdminBusinessInsightCharts';
import { formatInrAmount, formatIndianNumber } from '../../../utils/reportFormat';

export const BUSINESS_INSIGHT_MODULES = [
  { key: 'activity-summary', label: 'Activity & sales trend', description: 'Daily sales, weekday pattern, and fuel mix.', tone: 'primary', group: 'trends' },
  { key: 'staffing-analysis', label: 'Staffing coverage', description: 'Employee vs nozzle ratio with daily breakdown.', tone: 'info', group: 'operations' },
  { key: 'cash-irregularities', label: 'Cash irregularities', description: 'Daily short/excess trend and employee variance.', tone: 'danger', group: 'operations' },
  { key: 'expense-anomalies', label: 'Expense anomalies', description: '12-month expense spikes vs rolling average.', tone: 'warning', group: 'finance' },
  { key: 'profitability', label: 'Profitability check', description: 'Margin, expenses, profit score, and comparison.', tone: 'success', slow: true, group: 'finance' },
  { key: 'allocation-insights', label: 'Nozzle allocation', description: 'Employee–nozzle rank mismatch scoring.', tone: 'primary', group: 'operations' },
  { key: 'nozzle-utilization', label: 'Nozzle utilization', description: 'Share breakdown and over/under-used nozzles.', tone: 'info', group: 'operations' },
  { key: 'employee-performance', label: 'Employee performance', description: 'Sales per hour leaderboard with tiers.', tone: 'success', group: 'people' },
];

export const formatBiCurrency = (value) => formatInrAmount(value);

export const formatBiNumber = (value, digits = 2) => formatIndianNumber(value, digits);

const severityClass = (severity) => {
  const map = { critical: 'critical', warning: 'warning', info: 'info' };
  return map[severity] || 'info';
};

const staffingClass = (status) => {
  const map = { critical_understaff: 'critical', understaff: 'warning', overstaff: 'warning', balanced: 'info' };
  return map[status] || 'info';
};

export function BusinessInsightModuleResult({ moduleKey, data }) {
  if (!data) return null;
  if (data.message && !data.summary && !data.snapshot && !data.pairs) {
    return <p className="text-muted mb-0">{data.message}</p>;
  }

  if (moduleKey === 'activity-summary') {
    const s = data.summary || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Fuel sales</span><strong>{formatBiCurrency(s.fuel_sales)}</strong></div>
          <div className="admin-bi-kpi"><span>Active days</span><strong>{s.active_days} / {s.period_days}</strong></div>
          <div className="admin-bi-kpi"><span>Avg daily sales</span><strong>{formatBiCurrency(s.avg_daily_sales)}</strong></div>
          <div className="admin-bi-kpi"><span>Active nozzles</span><strong>{s.active_nozzles ?? 0}</strong></div>
          {s.sales_change_pct != null && (
            <div className="admin-bi-kpi">
              <span>vs prior period</span>
              <strong className={s.sales_change_pct >= 0 ? 'text-success' : 'text-danger'}>
                {s.sales_change_pct >= 0 ? '+' : ''}{formatBiNumber(s.sales_change_pct, 1)}%
              </strong>
            </div>
          )}
        </div>
        <ActivityCharts data={data} />
        <p className="small text-muted mb-0">
          Last sale: {s.last_sale_date || '—'} · Litres: {formatBiNumber(s.fuel_litres, 0)} L
          {s.best_day ? ` · Peak: ${s.best_day.label} (${formatBiCurrency(s.best_day.sales)})` : ''}
        </p>
      </>
    );
  }

  if (moduleKey === 'profitability') {
    const s = data.snapshot || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Profit score</span><strong>{s.profit_score} — {s.health_label}</strong></div>
          <div className="admin-bi-kpi"><span>Gross margin</span><strong>{formatBiCurrency(s.gross_margin)} ({formatBiNumber(s.gross_margin_pct, 2)}%)</strong></div>
          <div className="admin-bi-kpi"><span>Expenses</span><strong>{formatBiCurrency(s.total_expenses)} ({formatBiNumber(s.expense_ratio_pct, 2)}%)</strong></div>
          <div className="admin-bi-kpi"><span>Net operating</span><strong>{formatBiCurrency(s.net_operating)}</strong></div>
          <div className="admin-bi-kpi"><span>Net cash variance</span><strong className={s.net_cash_variance < 0 ? 'text-danger' : 'text-success'}>{formatBiCurrency(s.net_cash_variance)}</strong></div>
        </div>
        <ProfitabilityCharts data={data} />
      </>
    );
  }

  if (moduleKey === 'staffing-analysis') {
    const s = data.summary || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Emp / nozzle</span><strong>{formatBiNumber(s.emp_per_nozzle, 2)}</strong></div>
          <div className="admin-bi-kpi"><span>Avg employees</span><strong>{formatBiNumber(s.avg_active_employees, 1)}</strong></div>
          <div className="admin-bi-kpi"><span>Sales / employee</span><strong>{formatBiCurrency(s.sales_per_employee)}</strong></div>
          <div className="admin-bi-kpi"><span>Understaff days</span><strong>{s.understaff_days ?? 0}</strong></div>
          <div className="admin-bi-kpi"><span>Status</span><strong><span className={`admin-badge audit-severity ${staffingClass(s.status)}`}>{s.status_label}</span></strong></div>
        </div>
        <StaffingCharts data={data} />
        <p className="small mb-3">{s.recommendation}</p>
      </>
    );
  }

  if (moduleKey === 'expense-anomalies') {
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Months tracked</span><strong>{data.summary?.month_count ?? 0}</strong></div>
          <div className="admin-bi-kpi"><span>Anomalies</span><strong>{data.summary?.anomaly_count ?? 0}</strong></div>
          <div className="admin-bi-kpi"><span>Avg monthly</span><strong>{formatBiCurrency(data.summary?.avg_monthly_expense)}</strong></div>
        </div>
        <ExpenseAnomalyCharts data={data} />
      </>
    );
  }

  if (moduleKey === 'cash-irregularities') {
    const s = data.summary || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Cash short</span><strong className="text-danger">{formatBiCurrency(s.cash_short)}</strong></div>
          <div className="admin-bi-kpi"><span>Cash excess</span><strong className="text-success">{formatBiCurrency(s.cash_excess)}</strong></div>
          <div className="admin-bi-kpi"><span>Net variance</span><strong>{formatBiCurrency(s.net_cash_variance)}</strong></div>
          <div className="admin-bi-kpi"><span>Short % of sales</span><strong>{formatBiNumber(s.short_pct_of_sales, 3)}%</strong></div>
        </div>
        <CashCharts data={data} />
      </>
    );
  }

  if (moduleKey === 'allocation-insights') {
    const s = data.summary || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Allocation score</span><strong>{formatBiNumber(s.allocation_score, 1)}</strong></div>
          <div className="admin-bi-kpi"><span>Avg rank gap</span><strong>{formatBiNumber(s.avg_rank_gap, 2)}</strong></div>
        </div>
        <AllocationCharts data={data} />
        <p className="small mb-0">{s.recommendation}</p>
      </>
    );
  }

  if (moduleKey === 'nozzle-utilization') {
    const s = data.summary || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Total sales</span><strong>{formatBiCurrency(s.total_sales)}</strong></div>
          <div className="admin-bi-kpi"><span>Total litres</span><strong>{formatBiNumber(s.total_litres, 0)} L</strong></div>
          <div className="admin-bi-kpi"><span>Top nozzle</span><strong>{s.top_nozzle || '—'}</strong></div>
        </div>
        <NozzleCharts data={data} />
      </>
    );
  }

  if (moduleKey === 'employee-performance') {
    const s = data.summary || {};
    return (
      <>
        <InsightsPanel insights={data.insights} />
        <div className="admin-bi-kpi-row">
          <div className="admin-bi-kpi"><span>Team sales</span><strong>{formatBiCurrency(s.period_total_sales)}</strong></div>
          <div className="admin-bi-kpi"><span>Active attendants</span><strong>{s.active_employees ?? 0}</strong></div>
          <div className="admin-bi-kpi"><span>Avg ₹/hour</span><strong>{formatBiCurrency(s.avg_sales_per_hour)}</strong></div>
        </div>
        <EmployeeCharts data={data} />
      </>
    );
  }

  return null;
}

export function AdminPumpDiagnosticChecks({
  pumpId,
  pumpName = '',
  fromDate = '',
  toDate = '',
  dateRangeKey = 0,
  compact = false,
  moduleKeys = null,
}) {
  const modules = moduleKeys
    ? BUSINESS_INSIGHT_MODULES.filter((m) => moduleKeys.includes(m.key))
    : BUSINESS_INSIGHT_MODULES;
  const [moduleState, setModuleState] = useState({});
  const [batchRunning, setBatchRunning] = useState(false);

  useEffect(() => {
    setModuleState({});
  }, [pumpId, fromDate, toDate, dateRangeKey]);

  const canRun = Boolean(pumpId && fromDate && toDate);

  const runModule = (moduleKey, extra = {}) => {
    if (!canRun) return;

    setModuleState((prev) => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], loading: true, error: null },
    }));

    fetchAdminBusinessInsightsModule(pumpId, moduleKey, fromDate, toDate, extra)
      .then((data) => {
        if (data.status === false) {
          throw new Error(data.message || 'Check failed');
        }
        setModuleState((prev) => ({
          ...prev,
          [moduleKey]: { loading: false, error: null, data, loadedAt: new Date().toISOString() },
        }));
      })
      .catch((err) => {
        setModuleState((prev) => ({
          ...prev,
          [moduleKey]: { loading: false, error: friendlyFetchError(err), data: null },
        }));
      });
  };

  const runQuickChecks = () => {
    if (!canRun || batchRunning) return;
    setBatchRunning(true);
    const quick = modules.filter((m) => !m.slow);
    Promise.all(
      quick.map((mod) => {
        setModuleState((prev) => ({
          ...prev,
          [mod.key]: { ...prev[mod.key], loading: true, error: null },
        }));
        return fetchAdminBusinessInsightsModule(
          pumpId,
          mod.key,
          fromDate,
          toDate,
          mod.key === 'expense-anomalies' ? { months: 12 } : {}
        )
          .then((data) => {
            if (data.status === false) {
              throw new Error(data.message || 'Check failed');
            }
            setModuleState((prev) => ({
              ...prev,
              [mod.key]: { loading: false, error: null, data, loadedAt: new Date().toISOString() },
            }));
          })
          .catch((err) => {
            setModuleState((prev) => ({
              ...prev,
              [mod.key]: { loading: false, error: friendlyFetchError(err), data: null },
            }));
          });
      })
    ).finally(() => setBatchRunning(false));
  };

  return (
    <div className="admin-pump-diagnostics" id="pump-diagnostic-checks">
      <div className="admin-panel admin-pump-diagnostics-intro">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <h5 className="mb-1">Trends & diagnostic checks</h5>
            <p className="small text-muted mb-0">
              Run focused checks with charts and actionable insights — uses the same date range as the report above.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={!canRun || batchRunning}
              onClick={runQuickChecks}
            >
              {batchRunning ? 'Running…' : 'Run all quick checks'}
            </button>
            {!compact && (
              <Link to="/admin-business-insights" className="btn btn-sm btn-outline-secondary">
                Open full Business Insights
              </Link>
            )}
          </div>
        </div>
      </div>

      {modules.map((mod) => {
        const state = moduleState[mod.key] || {};
        return (
          <div key={mod.key} className="admin-panel mt-3 admin-bi-module admin-pump-diagnostic-module">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
              <div>
                <h6 className="mb-1">{mod.label}</h6>
                <p className="small text-muted mb-0">{mod.description}</p>
                {mod.slow && <p className="small text-warning mb-0">May take 30–60 seconds.</p>}
              </div>
              <button
                type="button"
                className={`btn btn-sm btn-${mod.tone}`}
                disabled={!canRun || state.loading || batchRunning}
                onClick={() => runModule(mod.key, mod.key === 'expense-anomalies' ? { months: 12 } : {})}
              >
                {state.loading ? 'Running…' : 'Run check'}
              </button>
            </div>

            {state.error && <div className="alert alert-danger py-2 small mb-2">{state.error}</div>}

            {state.data && (
              <div className="admin-bi-result">
                <div className="small text-muted mb-2">
                  {pumpName || `Pump ${pumpId}`} · {state.data.from_date} to {state.data.to_date}
                  {state.loadedAt ? ` · ${new Date(state.loadedAt).toLocaleString('en-IN')}` : ''}
                </div>
                <BusinessInsightModuleResult moduleKey={mod.key} data={state.data} />
              </div>
            )}

            {!state.data && !state.loading && !state.error && (
              <p className="small text-muted mb-0">Not run yet — click Run check or use Run all quick checks.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
